// recipe-env-source-merger.ts — phase 04b (spec V2 §4.2, B19/B22/N33).
//
// MỘT chỗ dựng env cho recipe — đúng 3 call site đo ở 04a:
//  - ticket (`supervisor-attempt-verify-input.ts`): `test.env` > shell;
//  - deploy + smoke/verify (`supervisor-release-approved-g3-leg.ts`, B22) —
//    review 04b H1: `test.env` do worker (cùng OS user) ghi được, nên với
//    release nó KHÔNG BAO GIỜ thắng shell: có stdin ⇒ stdin > shell (bỏ hẳn
//    `test.env`); không stdin ⇒ shell > `test.env`, và `test.env` không cấp key
//    điều khiển thực thi (release-recipe-execution-control-env-keys.ts).
// Luôn ∩ `env_allowlist` (theo thứ tự allowlist, như `buildAllowlistedEnv` của
// verification-recipe-runner.ts) ⇒ không có `test.env`/stdin thì env
// byte-identical v1 (§4.2 (1)). Recipe ticket KHÔNG có tham số stdin — không
// đường nào cấp được giá trị stdin cho nó.
//
// `test.env` chỉ qua `loadMissionTestEnv`, ĐỌC LẠI mỗi lần dựng env và nạp
// registry TRƯỚC khi trả (B19): mọi giá trị cấp cho recipe đã nằm trong
// known-values khi recipe in nó ra. Chỉ kết quả `ok` được cấp; quyền sai giữa
// run (N33) ⇒ recipe chạy như thiếu key (status cảnh báo — 04a L6). Map stdin
// cũng được hợp vào registry ở đây (chỉ thêm, idempotent) — không phụ thuộc
// caller đã nạp hay chưa.
import { addStdinValues } from "./mission-known-secret-values-registry.js";
import { loadMissionTestEnv } from "./mission-secrets-test-env-loader.js";
import { isExecutionControlEnvKey } from "./release-recipe-execution-control-env-keys.js";
import type { ReleaseSecretsStdin } from "./release-secrets-stdin-reader.js";

type EnvSource = Readonly<Record<string, string | undefined>>;

function mergeAllowlisted(
  allowlist: readonly string[],
  sourcesHighestFirst: readonly EnvSource[],
): Record<string, string> {
  const env: Record<string, string> = {};
  for (const key of allowlist) {
    for (const source of sourcesHighestFirst) {
      const value = Object.hasOwn(source, key) ? source[key] : undefined;
      if (value !== undefined) {
        env[key] = value;
        break;
      }
    }
  }
  return env;
}

async function testEnvValues(missionId: string): Promise<EnvSource> {
  try {
    const loaded = await loadMissionTestEnv(missionId);
    return loaded.status === "ok" ? loaded.values : {};
  } catch {
    // Không đọc được ⇒ như vắng file (recipe thiếu key), không làm chết run.
    return {};
  }
}

/** Env recipe ticket: `env_allowlist ∩ (test.env > shell)`. */
export async function ticketRecipeEnv(
  missionId: string,
  allowlist: readonly string[],
): Promise<Record<string, string>> {
  return mergeAllowlisted(allowlist, [await testEnvValues(missionId), process.env]);
}

/** `test.env` cho release (chỉ khi không có stdin): bỏ key điều khiển thực thi (H1). */
export function releaseUsableTestEnvValues(values: EnvSource): Record<string, string> {
  const usable: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && !isExecutionControlEnvKey(key)) usable[key] = value;
  }
  return usable;
}

export interface ReleaseRecipeEnvOptions {
  /** Tên recipe trong dòng stderr M-b (`deploy`/`smoke`). */
  recipeLabel?: string;
  /** Một dòng thông báo (chỉ TÊN key) — mặc định ghi stderr. */
  stderr?: (line: string) => void;
}

/**
 * Env recipe release (deploy + smoke/verify, sau G3), H1:
 * có stdin ⇒ `env_allowlist ∩ (stdin > shell)`;
 * không stdin ⇒ `env_allowlist ∩ (shell > test.env trừ key điều khiển thực thi)`.
 * M-b (review vòng sửa): không stdin mà `test.env` lấp key nào ⇒ in TÊN các key
 * đó ra stderr ngay trước spawn — người chạy thấy input worker ghi được tới release.
 */
export async function releaseRecipeEnv(
  missionId: string,
  allowlist: readonly string[],
  stdin: ReleaseSecretsStdin | undefined,
  options: ReleaseRecipeEnvOptions = {},
): Promise<Record<string, string>> {
  if (stdin !== undefined) {
    addStdinValues(missionId, { ...stdin });
    return mergeAllowlisted(allowlist, [stdin, process.env]);
  }
  const fromFile = releaseUsableTestEnvValues(await testEnvValues(missionId));
  const env = mergeAllowlisted(allowlist, [process.env, fromFile]);
  const fileKeys = Object.keys(env).filter(
    (key) => process.env[key] === undefined && Object.hasOwn(fromFile, key),
  );
  if (fileKeys.length > 0) {
    const write = options.stderr ?? ((line: string) => process.stderr.write(`${line}\n`));
    write(
      `⚠ recipe release "${options.recipeLabel ?? "release"}": key lấy từ test.env (shell không có): ` +
        `${fileKeys.join(", ")} — chạy --release-secrets-stdin để bỏ test.env.`,
    );
  }
  return env;
}
